import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import loadable from '@loadable/component';
import { useDispatch } from 'react-redux';
import { enableBodyScroll } from 'body-scroll-lock';
import { isDesktop, forDesktop } from '@westwing/ui-kit/breakPoints';
import { TrackHeaderActions } from '../../../client/ga/types';
import SearchInput from '../../../client/components/Generic/Input/SearchInput';
import { CrossIcon } from '../../../client/components/Generic/Icons/StyledIcons';
import {
    getSuggestBuckets,
    getBestSearchRedirect,
    getSuggestionFlatList,
    OrderedSearchBucketFlat,
} from '../../../client/components/Search/searchService';
import { MINIMUM_SEARCH_QUERY_LENGTH, SearchSuggestionBucketSelection } from '../../../client/redux/search/types';
import useTranslator from '../../../client/hooks/useTranslator';
import { useScrolLockRef } from '../../../client/components/Overlays/ScrollableArea';
import { ARROW_DOWN_KEY_CODE, ARROW_UP_KEY_CODE, ENTER_KEY_CODE } from '../../../client/constants/keyboards';
import {
    clearSearchSuggestionAction,
    fetchSearchSuggestionAction,
    fetchSearchRedirectsIfNeeded,
    setQueryAction,
} from '../../../client/redux/search/actions';
import useBellaPush from '../../../client/hooks/useBellaHistoryPush';
import useGa from '../../../client/hooks/useGa';
import { VictorQaClasses } from '../../../common/qaIds';
import useListingPageUrlState from '../../../client/hooks/ListingPage/useListingPageUrlState';
import { useOverlayOutsideClick } from '../../../client/hooks/dom/useOverlayOutsideClick';
import {
    SearchWrapper,
    SearchHeader,
    SearchText,
    SearchContainerWrapper,
    SearchContainer,
    CloseButton,
} from './StyledSearchComponents';
import { SearchProps } from '.';

const SuggestionLazy = loadable(() => import('./Suggestions'));
const BurgerMenuContent = loadable(() => import('../Overlays/BurgerFlyout/BurgerMenuContent'));

const StyledBurgerMenuContent = styled(BurgerMenuContent)`
    display: block;
    padding-bottom: 150px;
    ${forDesktop`
        display: none;
    `}
`;

const EMPTY_SEARCH = '';
const FIRST_ITEM_POSITION: number = -1;
const SUGGESTION_ITEM_DEFAULT: SearchSuggestionBucketSelection = {
    title: EMPTY_SEARCH,
    key: EMPTY_SEARCH,
    url: EMPTY_SEARCH,
};

const SearchContent: React.FC<SearchProps> = ({ suggestion, landingRedirects, searchRedirect }) => {
    const dispatch = useDispatch();
    const t = useTranslator();
    const bellaPush = useBellaPush();
    const gaTracking = useGa();

    const [urlState] = useListingPageUrlState();
    const searchQueryString = urlState.search || EMPTY_SEARCH;

    const [searchValue, setSearchValue] = useState<string>(searchQueryString);
    const [tempSearchValue, setTempSearchValue] = useState<string>(EMPTY_SEARCH);
    const [itemPosition, setItemPosition] = useState<number>(FIRST_ITEM_POSITION);
    const [selectedSuggestion, setSelectedSuggestion] = useState<SearchSuggestionBucketSelection>(
        SUGGESTION_ITEM_DEFAULT
    );
    const [isSearchActive, setSearchActive] = useState<boolean>(false);

    const scrollLockRef = useScrolLockRef(undefined, isSearchActive);
    const searchPlaceholder = t('Search text');

    const trackMobileSearchIconClick = () => {
        gaTracking.oneHeader.trackHeaderAction('searchiconclick', TrackHeaderActions.HeaderSearchIconClick);
    };

    const closeRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const clearSuggestions = () => {
        if (itemPosition !== FIRST_ITEM_POSITION) {
            setItemPosition(FIRST_ITEM_POSITION);
        }
        if (isSearchActive) {
            dispatch(clearSearchSuggestionAction());
        }
    };

    const enablePageScroll = () => {
        if (scrollLockRef.current) {
            enableBodyScroll(scrollLockRef.current);
        }
    };

    const resetHeader = () => {
        enablePageScroll();
        setSearchActive(false);
    };

    const closeSearchOverlayForMobile = () => {
        gaTracking.oneHeader.trackSearchClicks('dismiss search overlay');
        resetHeader();
    };

    const setSearchOverlayAsActive = () => {
        gaTracking.oneHeader.trackSearchClicks('Search bar click');
        clearSuggestions();
        setSearchActive(true);
        if (!isDesktop()) {
            trackMobileSearchIconClick();
        }
    };

    const wrapperContentRef = useOverlayOutsideClick(clearSuggestions, true);

    const clickOnClose = () => {
        if (closeRef.current) {
            closeRef.current.click();
            if (searchInputRef.current) {
                searchInputRef.current.blur();
            }
        }
    };

    const handleSuggestionLinkClick = (title: string) => {
        enablePageScroll();
        clearSuggestions();
        setSearchActive(false);
        dispatch(fetchSearchRedirectsIfNeeded());
        dispatch(setQueryAction(title));
    };

    const setItemSuggestedAndUpdatePosition = (
        item: SearchSuggestionBucketSelection = SUGGESTION_ITEM_DEFAULT,
        position: number = FIRST_ITEM_POSITION
    ) => {
        setItemPosition(position);
        setSelectedSuggestion(item);
        setSearchValue(item.title.toLowerCase());
    };

    const handleValueChange = (value: string): void => {
        setSearchValue(value);
        if (value.length > MINIMUM_SEARCH_QUERY_LENGTH) {
            setItemPosition(FIRST_ITEM_POSITION);
            dispatch(setQueryAction(value));
            dispatch(fetchSearchSuggestionAction());
        } else {
            clearSuggestions();
        }
    };

    const handleOnFocus = (): void => {
        if (searchValue.length > MINIMUM_SEARCH_QUERY_LENGTH) {
            dispatch(setQueryAction(searchValue));
        }
    };

    const orderedSuggestBucket = getSuggestBuckets(suggestion);
    const flatSuggestionItems: OrderedSearchBucketFlat = getSuggestionFlatList(orderedSuggestBucket);
    const lastItemPosition = flatSuggestionItems.length - 1;
    const hasSuggestion = flatSuggestionItems.length > 0;

    useEffect(() => {
        if (selectedSuggestion === SUGGESTION_ITEM_DEFAULT) {
            return;
        }
        if (flatSuggestionItems.some(bucket => bucket.title.toLowerCase() === searchValue)) {
            return;
        }
        setTempSearchValue(searchValue);
    }, [flatSuggestionItems, searchValue, selectedSuggestion]);

    useEffect(() => {
        SUGGESTION_ITEM_DEFAULT.title = tempSearchValue;
    }, [tempSearchValue]);

    useEffect(() => {
        setSearchValue(searchQueryString);
    }, [searchQueryString]);

    const keyCodeEventHandlerMap = {
        [ARROW_UP_KEY_CODE]: () => {
            if (itemPosition === 0) {
                return setItemSuggestedAndUpdatePosition();
            }

            if (itemPosition < 0) {
                return setItemSuggestedAndUpdatePosition(flatSuggestionItems[lastItemPosition], lastItemPosition);
            }
            const backwardItemPosition = itemPosition - 1;
            return setItemSuggestedAndUpdatePosition(flatSuggestionItems[backwardItemPosition], backwardItemPosition);
        },
        [ARROW_DOWN_KEY_CODE]: () => {
            if (itemPosition === lastItemPosition) {
                return setItemSuggestedAndUpdatePosition();
            }

            if (itemPosition > lastItemPosition) {
                return setItemSuggestedAndUpdatePosition(flatSuggestionItems[lastItemPosition], lastItemPosition);
            }
            const forwardItemPosition = itemPosition + 1;
            return setItemSuggestedAndUpdatePosition(flatSuggestionItems[forwardItemPosition], forwardItemPosition);
        },
        [ENTER_KEY_CODE]: () => {
            let { url } = selectedSuggestion;
            let isRedirect = false;
            const refreshBestSearchRedirect =
                searchValue.toLocaleLowerCase() !== selectedSuggestion.title.toLocaleLowerCase();
            if (refreshBestSearchRedirect) {
                ({ isRedirect, url } = getBestSearchRedirect(searchRedirect, landingRedirects, searchValue));
            }

            gaTracking.trackSearchLogs({
                action: gaTracking.actions.SearchLogs.SearchPerformed,
                label: searchValue,
            });

            bellaPush(url);
            clickOnClose();
            resetHeader();

            if (!refreshBestSearchRedirect) {
                dispatch(clearSearchSuggestionAction(true));
            }

            if (isRedirect) {
                gaTracking.trackSearch({
                    action: gaTracking.actions.Search.SearchRedirectWithoutSuggest,
                    label: searchValue,
                });
                // We have a tracker on the Listing page that handles SSR search terms
                // outside this component, this captures searches that redirect as well
                gaTracking.trackECommerce({
                    action: gaTracking.actions.ECommerce.PlpUrlSearchTerm,
                    label: searchValue.toLocaleLowerCase(),
                });
            }
        },
    };

    const handleKeyUp = (keyCode: number) => {
        const handler = keyCodeEventHandlerMap[keyCode];
        if (handler) {
            handler();
        }
    };
    const shouldShowCategoryMenu =
        (!searchValue || searchValue === searchQueryString) && isSearchActive && !hasSuggestion;

    return (
        <SearchWrapper data-testid="search-wrapper" isSearchActive={isSearchActive} ref={wrapperContentRef}>
            <SearchHeader role="heading" isSearchActive={isSearchActive}>
                <SearchText data-testid="search-overlay-title">{t('SearchOverlay_search')}</SearchText>
                <CloseButton onClick={closeSearchOverlayForMobile} ref={closeRef} data-testid="search-close-btn">
                    <CrossIcon />
                </CloseButton>
            </SearchHeader>
            <SearchInput
                isSearchActive={isSearchActive}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={handleValueChange}
                handleFocus={handleOnFocus}
                onKeyUp={handleKeyUp}
                handleTargetClick={setSearchOverlayAsActive}
                clearSuggestions={clearSuggestions}
                inputRef={searchInputRef}
            />
            <SearchContainerWrapper>
                <SearchContainer
                    role="contentinfo"
                    ref={!isDesktop() && isSearchActive ? scrollLockRef : null}
                    isSearchActive={isSearchActive}
                    className={VictorQaClasses.headerSearchSuggestionFlyout}
                >
                    {hasSuggestion && (
                        <SuggestionLazy
                            isSearchActive={isSearchActive}
                            selectedSuggestion={selectedSuggestion}
                            orderedSuggestions={orderedSuggestBucket}
                            additionalClickAction={handleSuggestionLinkClick}
                        />
                    )}
                    {shouldShowCategoryMenu && (
                        <StyledBurgerMenuContent
                            strict
                            title="SearchOverlay_categories"
                            bordered
                            hideCloseIcon
                            search
                            searchCloseFunction={resetHeader}
                        />
                    )}
                </SearchContainer>
            </SearchContainerWrapper>
        </SearchWrapper>
    );
};

export default SearchContent;
